import { useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { MobileModal } from 'components/UI/Modal/MobileModal'
import FbButton from 'components/FbUI/FbButton'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import apiService from 'services/api'
import to from 'await-to-js'
import { TextInput } from 'components/FormUtils/TextInput'
import { z } from 'zod'
import { CircleXIcon } from 'lucide-react'
import CreatableSelect from 'components/FormUtils/CreatableSelect'
import { StyledEditorRHF } from 'components/Editor/EditorRHF'
import axios from 'axios'

const formSchema = z.object({
  to: z.string({ message: 'Please select a recipient' }).email(),
  from_id: z.number({ message: 'Please select a from email' }),
  subject: z.string().min(1, 'Subject is required'),
  body: z.string().min(1, 'Email content is required'),
})

type FormValues = z.infer<typeof formSchema>

interface MobileDraftEmailModalProps {
  isOpen: boolean
  onClose: () => void
  contactCompanyId: number
  companyName?: string
}

export function MobileDraftEmailModal({
  isOpen,
  onClose,
  contactCompanyId,
  companyName,
}: MobileDraftEmailModalProps) {
  const api = apiService()
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Fetch email integrations (from addresses)
  const { data: emailIntegrationOptions } =
    api.emailIntegration.useGetEmailIntegrationOptions()

  // Fetch contacts for the company
  const { data: contactsData } = api.useGetContactsList(
    { contact_company_id: contactCompanyId, page_size: 100 },
    undefined,
    !!contactCompanyId
  )

  const {
    control,
    handleSubmit,
    formState: { isValid },
    setValue,
    reset,
  } = useForm<FormValues>({
    mode: 'onChange',
    resolver: zodResolver(formSchema),
    defaultValues: {
      to: '',
      subject: '',
      body: '',
    },
  })

  // Auto-select from email if only one integration
  useMemo(() => {
    if (emailIntegrationOptions?.length === 1) {
      setValue('from_id', emailIntegrationOptions[0].value)
    }
  }, [emailIntegrationOptions, setValue])

  // Build "To" options from contacts' emails
  const toOptions = useMemo(() => {
    const options: { label: string; value: string }[] = []
    const contacts = contactsData?.results || []

    contacts.forEach((contact) => {
      const name =
        `${contact.first_name || ''} ${contact.last_name || ''}`.trim()
      const emails = [
        contact.email_preferred,
        contact.email_work,
        contact.email_personal,
        contact.email_generic,
      ].filter(Boolean)

      emails.forEach((email) => {
        if (email) {
          options.push({
            label: name ? `${name} <${email}>` : email,
            value: email,
          })
        }
      })
    })

    // Remove duplicates
    return options.filter(
      (option, index, self) =>
        index === self.findIndex((o) => o.value === option.value)
    )
  }, [contactsData])

  const handleClose = () => {
    reset()
    onClose()
  }

  const onSubmit = handleSubmit(async (values) => {
    setIsSubmitting(true)

    try {
      const formData = new FormData()

      // Get selected contact info for the "to" field
      const selectedContact = contactsData?.results?.find((c) =>
        [
          c.email_preferred,
          c.email_work,
          c.email_personal,
          c.email_generic,
        ].includes(values.to)
      )

      formData.append(
        'to',
        JSON.stringify([
          {
            name: selectedContact
              ? `${selectedContact.first_name || ''} ${selectedContact.last_name || ''}`.trim()
              : '',
            email: values.to,
          },
        ])
      )
      formData.append('subject', values.subject)
      formData.append('body', values.body)

      const [err] = await to(
        api.emailIntegration.sendEmailMessage(values.from_id, formData)
      )

      setIsSubmitting(false)

      if (err) {
        if (axios.isAxiosError(err) && err.response?.data?.error) {
          toast.error(err.response.data.error)
        } else {
          toast.error('Failed to send email. Please try again.')
        }
        return
      }

      toast.success('Email sent successfully')
      handleClose()
    } catch (error) {
      setIsSubmitting(false)
      toast.error('Failed to send email. Please try again.')
    }
  })

  if (!isOpen) return null

  return (
    <MobileModal
      handleBack={handleClose}
      backLabel="Cancel"
      backIcon={<CircleXIcon size={20} />}
      header={
        <div className="p-2">
          <h1 className="text-2xl font-semibold text-gray-900">
            Send an Email
          </h1>
          {companyName && (
            <p className="mt-1 text-sm text-gray-600">{companyName}</p>
          )}
        </div>
      }
      footer={
        <div className="flex gap-4 p-2 justify-between">
          <div className="w-1/2">
            <FbButton
              className="w-full"
              type="button"
              size="mdlg"
              variant="secondary"
              onClick={handleClose}
            >
              Cancel
            </FbButton>
          </div>
          <div className="w-1/2">
            <FbButton
              className="w-full"
              type="button"
              size="mdlg"
              variant="primary"
              onClick={onSubmit}
              disabled={!isValid || isSubmitting}
            >
              {isSubmitting ? 'Sending...' : 'Send'}
            </FbButton>
          </div>
        </div>
      }
    >
      <div className="px-2 space-y-4">
        <CreatableSelect
          control={control}
          name="to"
          label="To"
          placeholder="Select recipient"
          options={toOptions}
          disableCreate={false}
          onCreateOption={() => Promise.resolve(null)}
          isClearable={false}
        />

        <CreatableSelect
          control={control}
          name="from_id"
          label="From"
          placeholder="Select your email"
          options={emailIntegrationOptions || []}
          disableCreate
          onCreateOption={() => Promise.resolve(null)}
          isClearable={false}
        />

        <TextInput
          control={control}
          name="subject"
          label="Subject"
          placeholder="Email subject"
        />

        <div className="min-h-[200px]">
          <StyledEditorRHF
            name="body"
            control={control}
            label="Content"
            placeholder="Write your email..."
            className="min-h-[150px]"
          />
        </div>
      </div>
    </MobileModal>
  )
}
